// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import type { Icon as IconType } from 'iconsax-react';
import {
  Activity,
  Add,
  AddSquare,
  ArrowLeft2,
  ArrowRight2,
  ArrowSwapHorizontal,
  ArrowUp2,
  ArrowDown2,
  Building,
  Calendar,
  Call,
  Category,
  ClipboardText,
  CloseCircle,
  Document,
  DocumentFavorite,
  DocumentText,
  DocumentUpload,
  Eraser,
  Flag,
  Flash,
  Forbidden2,
  Gallery,
  Global,
  HambergerMenu,
  Headphone,
  Home2,
  InfoCircle,
  Key,
  Link2,
  Link21,
  Location,
  Lock1,
  Logout,
  Microphone,
  MicrophoneSlash,
  Notification,
  Paperclip2,
  People,
  ProfileAdd,
  ProfileRemove,
  RotateLeft,
  RotateRight,
  SearchNormal1,
  Send2,
  ShieldCross,
  ShieldTick,
  Sort,
  Star1,
  TableDocument,
  TaskSquare,
  Copy,
  Heart,
  TextBold,
  TextItalic,
  TextUnderline,
  TextalignJustifyleft,
  Timer1,
  Trash,
} from 'iconsax-react';
import {
  ArchiveIcon,
  BellOutlineIcon,
  CalendarFieldIcon,
  ChangeOfAgencyIcon,
  ChartIcon,
  ChevronCompactDownIcon,
  ChevronCompactUpIcon,
  ChevronLeftIcon,
  ChevronLeftRoundedIcon,
  ChevronToggleIcon,
  CloseCircleOutlineIcon,
  CloseIcon,
  DashboardSquaresIcon,
  DocumentsIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  DownloadArrowIcon,
  EditIcon,
  EditTitleIcon,
  AddTitleIcon,
  EyeOpenOutlineIcon,
  EyeSlashOutlineIcon,
  FactFindIcon,
  PolicyIcon,
  TickCircleIcon,
  CheckmarkCircleIcon,
  TrashFieldIcon,
  TrashOutlineIcon,
  UserAddOutlineIcon,
  FigmaBanIcon,
  FigmaDownloadIcon,
  FigmaDownloadFilledIcon,
  FigmaEyeIcon,
  FigmaPlusSquareIcon,
  FigmaUploadIcon,
  FigmaUploadFilledIcon,
  FileCsvIcon,
  FileDocumentIcon,
  FileJpgIcon,
  FilePdfIcon,
  FilePngIcon,
  HamburgerMenuIcon,
  HandshakeIcon,
  HeadphonesOutlineIcon,
  HelpBubbleIcon,
  HistoryOutlineIcon,
  LogoutOutlineIcon,
  SettingsGearIcon,
  MoreHorizontalIcon,
  PlusFieldIcon,
  ProfileIcon,
  SeverityCheckIcon,
  SeverityExclamationIcon,
  SeverityInfoIcon,
  InfoCircleFilledIcon,
  SeverityWarningIcon,
  SquareCheckIcon,
  StatusArchiveIcon,
  StatusCheckboxIcon,
  StatusClockIcon,
  StatusDotIcon,
  StatusEnvelopeIcon,
  StatusRefreshIcon,
  StatusRejectedIcon,
  RejectionAlertIcon,
  UploadCloudArrowIcon,
  PolicySuccessCircleIcon,
  ExpandChevronDownIcon,
} from './icons';

const ICON_MAP: Record<string, IconType> = {
  // Navigation
  'angle double left': DoubleArrowLeftIcon,
  'angle double right': DoubleArrowRightIcon,
  'angle left': ArrowLeft2,
  'angle right': ArrowRight2,
  'arrow left': ArrowLeft2,
  'bars': HambergerMenu,
  'hamburger menu': HamburgerMenuIcon,
  'caret down': ArrowDown2,
  'caret up': ArrowUp2,
  'chevron compact down': ChevronCompactDownIcon,
  'chevron compact up': ChevronCompactUpIcon,
  'chevron down': ArrowDown2,
  'expand chevron': ExpandChevronDownIcon,
  'policy success': PolicySuccessCircleIcon,
  'chevron left': ChevronLeftIcon,
  'chevron left rounded': ChevronLeftRoundedIcon,
  'chevron toggle': ChevronToggleIcon,
  'chevron up': ArrowUp2,
  'dropdown': ArrowDown2,
  'home': Home2,

  // Actions
  'add': Add,
  'attach': Paperclip2,
  'close': CloseIcon,
  'close circle': CloseCircle,
  'close circle outline': CloseCircleOutlineIcon,
  'cloud upload': DocumentUpload,
  'download': FigmaDownloadIcon,
  'download arrow': DownloadArrowIcon,
  'download filled': FigmaDownloadFilledIcon,
  'change of agency': ChangeOfAgencyIcon,
  'edit': EditIcon,
  'edit outline': EditIcon,
  'edit title': EditTitleIcon,
  'add title': AddTitleIcon,
  'more': MoreHorizontalIcon,
  'more horizontal': MoreHorizontalIcon,
  'ellipsis horizontal': MoreHorizontalIcon,
  'eraser': Eraser,
  'exchange': ArrowSwapHorizontal,
  'pencil': EditIcon,
  'plus': Add,
  'plus field': PlusFieldIcon,
  'plus square outline': AddSquare,
  'figma plus square': FigmaPlusSquareIcon,
  'refresh': StatusRefreshIcon,
  'search': SearchNormal1,
  'send': Send2,
  'sign-out': Logout,
  'logout outline': LogoutOutlineIcon,
  'settings gear': SettingsGearIcon,
  'sort': Sort,
  'spinner': StatusRefreshIcon,
  'sort down': ArrowDown2,
  'sort up': ArrowUp2,
  'trash': Trash,
  'trash alternate outline': Trash,
  'trash field': TrashFieldIcon,
  'trash outline': TrashOutlineIcon,
  'upload': FigmaUploadIcon,
  'upload filled': FigmaUploadFilledIcon,
  'upload cloud arrow': UploadCloudArrowIcon,

  // Status
  'archive box': StatusArchiveIcon,
  'attention': StatusRejectedIcon,
  'ban': FigmaBanIcon,
  'check': StatusCheckboxIcon,
  'check circle': StatusCheckboxIcon,
  'checkbox': StatusCheckboxIcon,
  'clock': StatusClockIcon,
  'danger': StatusRejectedIcon,
  'envelope': StatusEnvelopeIcon,
  'exclamation circle': StatusRejectedIcon,
  'exclamation triangle': StatusRejectedIcon,
  'figma eye': FigmaEyeIcon,
  'figma download': FigmaDownloadIcon,
  'figma download filled': FigmaDownloadFilledIcon,
  'figma upload': FigmaUploadIcon,
  'figma upload filled': FigmaUploadFilledIcon,
  'figma ban': FigmaBanIcon,
  'forbidden': Forbidden2,
  'info circle': InfoCircle,
  'info circle filled': InfoCircleFilledIcon,
  'invite not sent': StatusEnvelopeIcon,
  'question circle': InfoCircle,
  'rejected': StatusRejectedIcon,
  'rejection alert': RejectionAlertIcon,
  'severity warning': SeverityWarningIcon,
  'severity exclamation': SeverityExclamationIcon,
  'severity check': SeverityCheckIcon,
  'severity info': SeverityInfoIcon,
  'square check': SquareCheckIcon,
  'status dot': StatusDotIcon,
  'shield': ShieldTick,
  'shield cross': ShieldCross,
  'times': CloseIcon,
  'times circle': CloseIcon,
  'warning circle': StatusRejectedIcon,
  'warning sign': StatusRejectedIcon,

  // Content / Documents
  'archive': StatusArchiveIcon,
  'archive drawer': ArchiveIcon,
  'clipboard list': ClipboardText,
  'clipboard outline': ClipboardText,
  'document favorite': DocumentFavorite,
  'file': Document,
  'file document': FileDocumentIcon,
  'help bubble': HelpBubbleIcon,
  'file alternate': DocumentText,
  'file alternate outline': DocumentText,
  'file csv': FileCsvIcon,
  'file csv outline': FileCsvIcon,
  'file excel': TableDocument,
  'file excel outline': TableDocument,
  'file image': Gallery,
  'file image outline': Gallery,
  'file jpg': FileJpgIcon,
  'file jpg outline': FileJpgIcon,
  'file outline': Document,
  'file pdf': FilePdfIcon,
  'file pdf outline': FilePdfIcon,
  'file png': FilePngIcon,
  'file png outline': FilePngIcon,
  'file text outline': DocumentText,
  'file word': DocumentText,
  'file word outline': DocumentText,
  'wpforms': DocumentText,

  // Entity / People
  'building': Building,
  'building outline': Building,
  'handshake': HandshakeIcon,
  'remove user': ProfileRemove,
  'user': ProfileIcon,
  'user outline': ProfileIcon,
  'user plus': ProfileAdd,
  'user add outline': UserAddOutlineIcon,
  'user times': ProfileRemove,
  'users': People,

  // Communication
  'bell': Notification,
  'bell outline': BellOutlineIcon,
  'inbox': StatusEnvelopeIcon,
  'mail': StatusEnvelopeIcon,
  'mail outline': StatusEnvelopeIcon,
  'phone': Call,

  // Data / Charts
  'chart': ChartIcon,
  'chart bar': ChartIcon,
  'chart line': ChartIcon,
  'dashboard': Category,
  'dashboard squares': DashboardSquaresIcon,
  'documents': DocumentsIcon,
  'fact find': FactFindIcon,
  'policy': PolicyIcon,
  'heartbeat': Activity,
  'heart': Heart,
  'copy': Copy,

  // Misc
  'calendar': Calendar,
  'calendar field': CalendarFieldIcon,
  'clock outline': StatusClockIcon,
  'eye': FigmaEyeIcon,
  'eye open outline': EyeOpenOutlineIcon,
  'eye slash outline': EyeSlashOutlineIcon,
  'tick circle': TickCircleIcon,
  'checkmark circle': CheckmarkCircleIcon,
  'flag': Flag,
  'globe': Global,
  'headphones': Headphone,
  'headphones outline': HeadphonesOutlineIcon,
  'history': StatusClockIcon,
  'history outline': HistoryOutlineIcon,
  'key': Key,
  'lightning': Flash,
  'linkify': Link21,
  'list ol': TaskSquare,
  'list ul': TextalignJustifyleft,
  'lock': Lock1,
  'map marker alternate': Location,
  'microphone': Microphone,
  'microphone slash': MicrophoneSlash,
  'star': Star1,
  'timer': Timer1,
  'bold': TextBold,
  'italic': TextItalic,
  'underline': TextUnderline,
  'undo': RotateLeft,
  'redo': RotateRight,
  'unlinkify': Link2,
};

const COLOR_MAP: Record<string, string> = {
  red: '#db2828',
  orange: '#f2711c',
  yellow: '#fbbd08',
  green: '#21ba45',
  blue: '#2185d0',
  grey: '#767676',
  gray: '#767676',
  white: '#ffffff',
  black: '#1b1c1d',
};

const SIZE_MAP: Record<string, number> = {
  mini: 12,
  tiny: 14,
  small: 16,
  large: 24,
  big: 28,
  huge: 36,
  massive: 48,
};

export interface AppIconProps {
  name: string;
  size?: number | 'mini' | 'tiny' | 'small' | 'large' | 'big' | 'huge' | 'massive';
  color?: string;
  variant?: 'Linear' | 'Outline' | 'Broken' | 'Bold' | 'Bulk' | 'TwoTone';
  className?: string;
  style?: React.CSSProperties;
}

export function AppIcon({
  name,
  size = 20,
  color,
  variant = 'Linear',
  className,
  style,
}: AppIconProps) {
  const IconComponent = ICON_MAP[name];

  if (!IconComponent) {
    console.warn(`AppIcon: Unknown icon name "${name}"`);
    return null;
  }

  const resolvedSize = typeof size === 'string' ? SIZE_MAP[size] ?? 20 : size;
  const resolvedColor = color ? (COLOR_MAP[color] ?? color) : 'currentColor';

  return (
    <IconComponent
      size={resolvedSize}
      color={resolvedColor}
      variant={variant}
      className={className}
      style={style}
      data-icon={name}
    />
  );
}
